import config from '../../config/config';
import axios from 'axios';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import { ActionPreferences, ContactPerson } from '@lemontaps/lib/types/api';
import { withSilentAccessToken } from '../helper';
import { PERMISSIONS } from '../constants';
import { VCardPurpose } from '@lemontaps/lib/business-logic/features/vcard/types';
import { BeprintAvailability } from '@lemontaps/lib/business-logic/integrations/beprint/types';

export const checkDomain = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + `account/checkDomain`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
};

export const getOwnAccount = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + 'account', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
};

export const getOwnAccountCrossDomain = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + 'account/crossDomain', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
};

export const getVcard = async (
  accountId: string,
  lang: string,
  purpose: VCardPurpose = 'plain',
) => {
  return await axios.get(config.API_BASE_URL + 'account/download/contact', {
    params: {
      accountId,
      lang,
      purpose,
      response: 'string',
    },
  });
};

export const postActionPreference = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  partialPrefs: Partial<ActionPreferences>,
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(
      `${config.API_BASE_URL}account/action-preferences`,
      {
        actionPreferences: partialPrefs,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );
};

export const getWalletPass = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  qrType: 'online' | 'offline',
  wallet: 'apple' | 'google',
  lang?: string,
  uidForProfileLink?: number,
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(
      config.API_BASE_URL +
        `account/pass/${wallet}?qrType=${qrType}` +
        (uidForProfileLink ? `&uid=${uidForProfileLink}` : '') +
        (lang ? `&lang=${lang}` : ''),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // for `apple` wallet, we get a buffer response
        // for `google` wallet, we get a url string response
        ...(wallet === 'apple' && { responseType: 'blob' }),
      },
    ),
  );
};

export const deleteAccount = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  email: string,
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(
      config.API_BASE_URL + `account/delete`,
      { authEmail: email },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );
};

export const getAccountCodes = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  const { data } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.get(config.API_BASE_URL + 'account/codes', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),

    [PERMISSIONS.READ.THEME_PROFILES],
  );
  return data;
};

export const deleteAccountCode = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  code: string,
) => {
  const { data } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.delete(config.API_BASE_URL + `account/codes/${code}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.WRITE.THEME_PROFILES],
  );
  if (!data.isSuccess) {
    throw new Error('Failed to delete account code');
  }
};

export const connectCode = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  code: string,
) => {
  const { data } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.post(
        config.API_BASE_URL + 'account/addcode',
        { code },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      ),
    [PERMISSIONS.WRITE.THEME_PROFILES],
  );

  if (!data.isSuccess) {
    throw new Error('Failed to connect code');
  }
  return data;
};

export const getBlockedFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  const { data } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + 'theme/blocked-bio-fields', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
  if (!data.isSuccess) {
    throw new Error('Failed to fetch blocked fields');
  }
  return data;
};

export const getScimBlockedFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(`${config.API_BASE_URL}idps/scim/blockedFields`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
};

export const getBeprintSsoUrl = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  variant: 'product' | 'approval',
  accountId?: string, // only relevant if admin logs in as different user
) => {
  return await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get<{ data: { ssoUrl: string; ssoError: null | undefined | string } }>(
      `${config.API_BASE_URL}account/beprint-login`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { variant, accountId },
      },
    ),
  );
};

export const getIsBeprintAvailable = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
): Promise<BeprintAvailability> => {
  try {
    const res = await withSilentAccessToken(getAccessTokenSilently, token =>
      axios.get<{
        data: BeprintAvailability;
      }>(`${config.API_BASE_URL}account/beprint-available`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    );
    return res?.data?.data;
  } catch {
    return {
      isAvailable: false,
      isUserOrderAvailable: false,
      isAdminOrderAvailable: false,
      isApprovalAvailable: false,
    };
  }
};

export const getChangeRequestedFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  const { data } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + 'account/change-requested-fields', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
  if (!data.isSuccess) {
    throw new Error('Failed to fetch change requested fields');
  }
  return data;
};

export const getContactPersons = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  const { data } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + 'theme/contact-persons', {
      headers: { Authorization: `Bearer ${token}` },
    }),
  );

  return data as ContactPerson[];
};
